import type { AssetId, ContentId, GameId, SessionId, StageId } from '../../shared/types/ids';
import type { GameSessionSummary } from '../../shared/types/session';

/**
 * The typed React↔Phaser bridge (architecture §6). React owns the shell; Phaser owns the
 * stage. They exchange these events and nothing else — no shared mutable state, no reaching
 * into each other's internals.
 *
 * The `game:*` and `parent:*` keys are the documented set. Phase 0 adds `shell:*` requests
 * (React asking the stage for something) and the acknowledgements the stage sends back, using
 * the same `namespace:verb` convention.
 */

export interface ParentOverrideCommand {
  type: 'SKIP_ROUND' | 'SIMPLIFY' | 'RESTART_CALIBRATION' | 'END_SESSION';
  reason?: string;
}

/** What the stage actually rendered for a requested asset ID. */
/** Why nothing is on screen for an asset ID. Present only when `failed` is true. */
export type AssetFailureReason =
  /** Every candidate in the chain was tried and none loaded. */
  | 'chain-exhausted'
  /** The ID was already loading, so this request was refused rather than silently dropped. */
  | 'duplicate-in-flight';

export interface AssetResolutionReport {
  requestedAssetId: AssetId;
  /** The candidate the stage last tried. Meaningful only while `failed` is false. */
  resolvedAssetId: AssetId;
  usedFallback: boolean;
  /**
   * Nothing is on screen for this ID. Reporting the last attempt as a success instead was how a
   * missing texture came to be listed as resolved (review R3-7).
   */
  failed: boolean;
  failureReason?: AssetFailureReason;
}

export interface ThriveAppEventMap {
  'game:ready': { gameId: GameId };
  'game:started': { sessionId: SessionId };
  'game:paused': { reason: string };
  'game:resumed': { reason: string };
  'game:completed': { summary: GameSessionSummary };
  'game:error': { code: string; recoverable: boolean };
  'parent:override': ParentOverrideCommand;

  'shell:pause-requested': { reason: string };
  'shell:resume-requested': { reason: string };
  'shell:exit-requested': { reason: string };
  /**
   * "Say that again." A child who missed the instruction asks for it back (GDS §31); the
   * keyboard simulation binds it to R (D-006). It is a request, not a command: the narration
   * layer decides whether anything is repeatable right now.
   */
  'shell:repeat-narration-requested': { reason: string };

  /**
   * Round lifecycle, from the mission runtime to whatever is drawing (D-034 PS-3).
   *
   * A scene renders what these carry. It is never told the expected response and never asked
   * whether an answer was right — that is the platform's decision, taken from the item's
   * `ExpectedResponse`, which is what keeps the keyboard and camera paths identical.
   */
  'mission:round-presented': {
    roundIndex: number;
    stageId: StageId;
    contentId: ContentId;
    attemptNumber: number;
    simplified: boolean;
    promptText: string;
    /** Answer labels, in zone order where the mechanic has zones. Never marked correct. */
    choiceLabels: string[];
  };
  'mission:round-resolved': {
    roundIndex: number;
    contentId: ContentId;
    completed: boolean;
    shardsAwarded: number;
  };
  /** Extra help while a round is open. Never a failure signal. */
  'mission:assist': { roundIndex: number; assist: string; text?: string };
  'mission:score-changed': { label: string; value: number; progress: number };

  'game:scene-ready': { gameId: GameId; sceneKey: string };
  'game:exit-acknowledged': { sceneKey: string };
  /** Most recent semantic input lines, newest last. Display only. */
  'game:input-observed': { lines: string[] };
  'game:assets-resolved': { resolutions: AssetResolutionReport[] };
}

export type ThriveAppEventType = keyof ThriveAppEventMap;

export type ThriveAppEventListener<TType extends ThriveAppEventType> = (
  payload: ThriveAppEventMap[TType],
) => void;

export type Unsubscribe = () => void;

export interface ThriveAppEventBus {
  on<TType extends ThriveAppEventType>(
    type: TType,
    listener: ThriveAppEventListener<TType>,
  ): Unsubscribe;

  emit<TType extends ThriveAppEventType>(type: TType, payload: ThriveAppEventMap[TType]): void;

  clear(): void;
  listenerCount(type?: ThriveAppEventType): number;
}

type AnyListener = (payload: never) => void;

export function createThriveAppEventBus(): ThriveAppEventBus {
  const listeners = new Map<ThriveAppEventType, Set<AnyListener>>();

  return {
    on<TType extends ThriveAppEventType>(
      type: TType,
      listener: ThriveAppEventListener<TType>,
    ): Unsubscribe {
      let bucket = listeners.get(type);
      if (bucket === undefined) {
        bucket = new Set();
        listeners.set(type, bucket);
      }
      bucket.add(listener as AnyListener);

      return () => {
        const current = listeners.get(type);
        if (current === undefined) return;
        current.delete(listener as AnyListener);
        if (current.size === 0) listeners.delete(type);
      };
    },

    emit<TType extends ThriveAppEventType>(type: TType, payload: ThriveAppEventMap[TType]): void {
      const bucket = listeners.get(type);
      if (bucket === undefined) return;
      // Snapshot so a listener unsubscribing during dispatch cannot skip its neighbour.
      for (const listener of [...bucket]) {
        if (!bucket.has(listener)) continue;
        (listener as ThriveAppEventListener<TType>)(payload);
      }
    },

    clear(): void {
      listeners.clear();
    },

    listenerCount(type?: ThriveAppEventType): number {
      if (type === undefined) {
        let total = 0;
        for (const bucket of listeners.values()) total += bucket.size;
        return total;
      }
      return listeners.get(type)?.size ?? 0;
    },
  };
}
